import { BrowserRouter } from 'react-router-dom'
import { AppRoutes } from './router'
import { AuthProvider } from './contexts/AuthContext'
import { AdminAuthProvider } from './contexts/AdminAuthContext'
import { useEffect } from 'react'

function App() {
  useEffect(() => {
    // Handle auth token backup on page unload
    const handleBeforeUnload = () => {
      const signedOut = sessionStorage.getItem('user-signed-out')
      if (signedOut) return

      const authToken = localStorage.getItem('sb-auth-token')
      if (authToken) {
        try {
          sessionStorage.setItem('sb-auth-token-backup', authToken)
          sessionStorage.setItem('sb-auth-backup-timestamp', Date.now().toString())
        } catch (error) {
          console.error('Error backing up auth token:', error)
        }
      }
    }

    // Restore auth token on page load
    const handleLoad = () => {
      try {
        const signedOut = sessionStorage.getItem('user-signed-out')
        if (signedOut) {
          // Clear all auth tokens if user signed out
          localStorage.removeItem('sb-auth-token')
          sessionStorage.removeItem('sb-auth-token-backup')
          sessionStorage.removeItem('sb-auth-backup-timestamp')
          sessionStorage.removeItem('backup_sb-auth-token')
          return
        }

        const currentToken = localStorage.getItem('sb-auth-token')
        const backupToken = sessionStorage.getItem('sb-auth-token-backup')
        const backupTimestamp = sessionStorage.getItem('sb-auth-backup-timestamp')
        
        // Check if backup is recent (within 24 hours)
        const isRecentBackup = backupTimestamp && 
          (Date.now() - parseInt(backupTimestamp)) < 24 * 60 * 60 * 1000
        
        if (!currentToken && backupToken && isRecentBackup) {
          localStorage.setItem('sb-auth-token', backupToken)
        }
      } catch (error) {
        console.error('Error restoring auth token:', error)
      }
    }

    // Handle storage changes from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'sb-auth-token') {
        if (!event.newValue && event.oldValue) {
          // Token was removed - user signed out in another tab
          sessionStorage.setItem('user-signed-out', 'true')
          window.location.reload()
        } else if (event.newValue && !event.oldValue) {
          // Token was added - user signed in in another tab
          sessionStorage.removeItem('user-signed-out')
          window.location.reload()
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('load', handleLoad)
    window.addEventListener('storage', handleStorageChange)

    // Run restoration on initial load
    handleLoad()

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('load', handleLoad)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  return (
    <AuthProvider>
      <AdminAuthProvider>
        <BrowserRouter basename={__BASE_PATH__}>
          <AppRoutes />
        </BrowserRouter>
      </AdminAuthProvider>
    </AuthProvider>
  )
}

export default App
